import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiErrorMessage, useInviteColleague } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Invite a colleague to the account. Email is their MCP sign-in identity, so
 * it's required. A 409 (email already on the account) surfaces inline.
 */
export function InviteColleagueDialog({ open, onOpenChange }: Props) {
  const inviteColleague = useInviteColleague();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setFullName("");
      setEmail("");
      setRole("");
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await inviteColleague.mutateAsync({
        fullName: fullName.trim(),
        email: email.trim(),
        role: role.trim() || undefined,
      });
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't send the invite. Please try again."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a colleague</DialogTitle>
          <DialogDescription>
            Add someone at your organization. They&apos;ll sign in with the email you provide.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="colleagueFullName" className="text-foreground text-sm font-medium">
              Full name
            </label>
            <Input
              id="colleagueFullName"
              placeholder="Dr. Jane Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="colleagueEmail" className="text-foreground text-sm font-medium">
              Email <span className="text-muted font-normal">(their sign-in)</span>
            </label>
            <Input
              id="colleagueEmail"
              type="email"
              placeholder="jane.doe@yourcompany.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="colleagueRole" className="text-foreground text-sm font-medium">
              Role <span className="text-muted font-normal">(optional)</span>
            </label>
            <Input
              id="colleagueRole"
              placeholder="Procurement lead"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={inviteColleague.isPending || !fullName.trim() || !email.trim()}
            >
              {inviteColleague.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Send invite"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
